/**
 * ============================================================================
 * pageLinkPicker — Bridge entre o editor Tiptap (extensões estáticas) e a
 *                  página React (que monta os modais reais).
 *
 * Expõe DUAS pontes independentes:
 *   1) openPageLinkPicker  — LEGADO. Modal simples de escolha de artigo.
 *      Ainda usado por locais que só querem inserir link de artigo direto
 *      sem trocar rótulo/texto.
 *   2) openLinkEditor      — NOVO (v1.1.0). Editor completo de link
 *      (externo OU interno: space > página > seção). Recebe `initial` para
 *      abrir em modo edição de um link existente.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.1.0 - Adiciona bridge openLinkEditor para o novo
 *            KbLinkEditorDialog (padrão GitBook: editar/inserir URL externa
 *            ou wiki-link com seleção de space, página e seção).
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { logger } from "@/lib/logger";
import type { KbLinkSearchResult } from "@/hooks/kb/useKbSearchPagesForLink";

// ---------------------------------------------------------------------------
// LEGADO: seletor simples de artigo
// ---------------------------------------------------------------------------
export type PageLinkPickerHandler = (onPick: (page: KbLinkSearchResult) => void) => void;

let currentHandler: PageLinkPickerHandler | null = null;

export function setPageLinkPickerHandler(handler: PageLinkPickerHandler | null): void {
  currentHandler = handler;
}

export function openPageLinkPicker(onPick: (page: KbLinkSearchResult) => void): boolean {
  if (!currentHandler) {
    logger.warn("[pageLinkPicker]", "Nenhum handler registrado — picker indisponível");
    return false;
  }
  currentHandler(onPick);
  return true;
}

// ---------------------------------------------------------------------------
// NOVO: editor completo de link (padrão GitBook)
// ---------------------------------------------------------------------------

/** Tipo do valor inicial passado para o editor de link. */
export type LinkEditorInitial =
  | { kind: "none"; text?: string }                                        // inserir novo (sem seleção)
  | { kind: "external"; href: string; text?: string }                      // editando link externo
  | {                                                                       // editando wiki-link
      kind: "page";
      pageId: string;
      spaceId?: string | null;
      anchor?: string | null;
      snapshotTitle?: string | null;
      text?: string;
    };

/** Resultado final quando o usuário confirma o modal. */
export type LinkEditorResult =
  | { kind: "external"; href: string; text?: string }
  | {
      kind: "page";
      pageId: string;
      spaceId: string;
      anchor?: string | null;
      title: string;      // rótulo visível a inserir/substituir
    }
  | { kind: "remove" };   // usuário clicou em "Remover link"

export type LinkEditorHandler = (
  initial: LinkEditorInitial,
  onSubmit: (result: LinkEditorResult) => void,
) => void;

let linkEditorHandler: LinkEditorHandler | null = null;

export function setLinkEditorHandler(handler: LinkEditorHandler | null): void {
  linkEditorHandler = handler;
}

export function openLinkEditor(
  initial: LinkEditorInitial,
  onSubmit: (result: LinkEditorResult) => void,
): boolean {
  if (!linkEditorHandler) {
    logger.warn("[pageLinkPicker]", "Nenhum handler de link editor registrado");
    return false;
  }
  linkEditorHandler(initial, onSubmit);
  return true;
}
